/**
 * THE RESEARCH SPACES CLIENT — the Owner's own configuration surface.
 *
 * A space is a persistent research identity over the ONE Scout engine. This
 * module creates and configures one: its Vision, Mission and Perspective, its
 * objective/evidence trace, its integrations, its schedule and its bounded
 * research authorization. It does not contain a second chat client: the
 * dedicated conversation is the ordinary Chat surface at `/chat/space/<id>`.
 */

import { readGateway, type GatewayResult } from './gateway';

export interface ResearchSpace {
	spaceId: string;
	name: string;
	slug: string;
	status: 'ACTIVE' | 'PAUSED' | 'ARCHIVED';
	configVersion: number;
	schedule: SpaceSchedule | null;
	budget: { maxUsdPerRun: number; maxUsdPerDay: number | null } | null;
	lastScheduleSlot: string | null;
	lastScheduleStartedAt: string | null;
	lastRunId: string | null;
	createdAt: string;
	updatedAt: string;
}

export interface SpaceSchedule {
	mode: 'MANUAL' | 'SCHEDULED' | 'MANDATE';
	enabled: boolean;
	days: number[];
	time: string;
	timezone: string;
	durationMinutes: number;
	budgetUsd: number;
	until?: string | null;
}

export interface SpaceDocument {
	spaceId: string;
	layer: 'VISION' | 'MISSION' | 'PERSPECTIVE';
	version: number;
	content: string;
	authoredBy: 'OWNER' | 'SCOUT';
	status: 'ACTIVE' | 'SUPERSEDED';
	provenance: string;
	createdAt: string;
}

export interface SpaceObjective {
	objectiveId: string;
	statement: string;
	status:
		| 'NOT_TESTED'
		| 'INVESTIGATING'
		| 'SUPPORTED'
		| 'REFUTED'
		| 'VALIDATED'
		| 'GOAL_ACHIEVED'
		| 'BLOCKED';
	statusNote: string | null;
	evidenceRefs: string[];
	createdAt: string;
	updatedAt: string;
}

export interface SpaceIntegration {
	integrationId: string;
	kind: 'GITHUB_REPOSITORY' | 'MODEL_PROVIDER' | 'SEARCH_PROVIDER';
	provider: string;
	config: Record<string, unknown>;
	status: 'AUTHORIZED' | 'NOT_CONFIGURED' | 'NOT_AUTHORIZED' | 'REVOKED';
	configured: boolean;
	reason: string | null;
	scope: string;
	authorizedAt: string | null;
	createdAt: string;
	updatedAt: string;
}

export interface SpaceRun {
	runId: string;
	state: string;
	authorizationText: string;
	requestedMs: number;
	deadlineAt: string;
	startedAt: string;
	endedAt: string | null;
	terminalReason: string | null;
	segmentsRun: number;
	/** THE CONTINUOUS LANE'S OWN COUNTER. `segmentsRun` predates it and can be
	 *  0 for a window that executed steps; the table states both. */
	stepsCompleted?: number;
	spaceId?: string | null;
}

export interface SpaceKnowledgeEntry {
	entryId: string;
	title: string;
	learned: string;
	whyItMatters: string;
	limitations: string;
	knowledgeStatus: string;
	spaceId: string | null;
	publishedToGlobal?: boolean;
	evidenceRefs: string[];
	createdAt: string;
}

export interface SpaceTrial {
	trialId: string;
	provider: string;
	model: string;
	status: string;
	responseExcerpt: string | null;
	inputTokens: number | null;
	outputTokens: number | null;
	usageSource: string | null;
	costMicrousd: number | null;
	detail: string | null;
	createdAt: string;
}

export interface SpaceDetail {
	space: ResearchSpace;
	documents: {
		VISION: SpaceDocument | null;
		MISSION: SpaceDocument | null;
		PERSPECTIVE: SpaceDocument | null;
	};
	objectives: SpaceObjective[];
	integrations: SpaceIntegration[];
	runs: SpaceRun[];
	episodes: Array<{
		episodeId: string;
		question: string;
		status: string;
		createdAt: string;
		spaceId?: string | null;
	}>;
	knowledge: SpaceKnowledgeEntry[];
	trials: SpaceTrial[];
	chat: { conversationId: string; createdAt: string } | null;
	nextSlot: { at: string; slot: string } | null;
	dueSlot: { at: string; slot: string } | null;
	contextPreview: string;
	ok?: true;
	wired?: true;
}

export async function listResearchSpaces(): Promise<
	GatewayResult<{ ok: boolean; wired: boolean; spaces?: ResearchSpace[]; reason?: string }>
> {
	return readGateway('/api/scout/gateway/spaces', { method: 'GET', timeoutMs: 30_000 });
}

export async function createResearchSpace(input: {
	name: string;
	what: string;
	why: string;
	outcome: string;
	vision?: string;
	mission?: string;
	objectives?: string[];
}): Promise<GatewayResult<{ ok: boolean; space?: ResearchSpace; detail?: SpaceDetail }>> {
	return readGateway('/api/scout/gateway/spaces', {
		method: 'POST',
		body: input,
		timeoutMs: 60_000,
	});
}

export async function readResearchSpace(
	spaceId: string,
): Promise<GatewayResult<SpaceDetail & { ok: boolean; wired: boolean; reason?: string }>> {
	return readGateway(`/api/scout/gateway/spaces/${encodeURIComponent(spaceId)}`, {
		method: 'GET',
		timeoutMs: 30_000,
	});
}

export async function updateResearchSpace(
	spaceId: string,
	patch: {
		name?: string;
		status?: 'ACTIVE' | 'PAUSED' | 'ARCHIVED';
		schedule?: SpaceSchedule | null;
		budget?: { maxUsdPerRun: number; maxUsdPerDay: number | null } | null;
	},
): Promise<GatewayResult<{ ok: boolean; space?: ResearchSpace }>> {
	return readGateway(`/api/scout/gateway/spaces/${encodeURIComponent(spaceId)}`, {
		method: 'PATCH',
		body: patch,
		timeoutMs: 30_000,
	});
}

export async function saveSpaceDocument(
	spaceId: string,
	input: {
		layer: 'VISION' | 'MISSION' | 'PERSPECTIVE';
		content: string;
		provenance?: string;
	},
): Promise<GatewayResult<{ ok: boolean; document?: SpaceDocument }>> {
	return readGateway(`/api/scout/gateway/spaces/${encodeURIComponent(spaceId)}/documents`, {
		method: 'POST',
		body: input,
		timeoutMs: 30_000,
	});
}

export async function saveSpaceObjective(
	spaceId: string,
	input: {
		objectiveId?: string;
		statement: string;
		status: SpaceObjective['status'];
		statusNote?: string;
		evidenceRefs?: string[];
	},
): Promise<GatewayResult<{ ok: boolean; objective?: SpaceObjective }>> {
	return readGateway(`/api/scout/gateway/spaces/${encodeURIComponent(spaceId)}/objectives`, {
		method: 'POST',
		body: input,
		timeoutMs: 30_000,
	});
}

export async function saveSpaceIntegration(
	spaceId: string,
	input: {
		kind: SpaceIntegration['kind'];
		provider: string;
		config: Record<string, unknown>;
	},
): Promise<GatewayResult<{ ok: boolean; integration?: SpaceIntegration }>> {
	return readGateway(`/api/scout/gateway/spaces/${encodeURIComponent(spaceId)}/integrations`, {
		method: 'POST',
		body: input,
		timeoutMs: 30_000,
	});
}

export async function revokeSpaceIntegration(
	spaceId: string,
	integrationId: string,
): Promise<GatewayResult<{ ok: boolean; integration?: SpaceIntegration }>> {
	return readGateway(
		`/api/scout/gateway/spaces/${encodeURIComponent(spaceId)}/integrations/${encodeURIComponent(integrationId)}/revoke`,
		{ method: 'POST', body: {}, timeoutMs: 30_000 },
	);
}

export async function authorizeSpaceRun(
	spaceId: string,
	input: { durationMinutes: number; budgetUsd?: number; authorizationText?: string },
): Promise<GatewayResult<{ ok: boolean; run?: SpaceRun; space?: ResearchSpace; reason?: string }>> {
	return readGateway(`/api/scout/gateway/spaces/${encodeURIComponent(spaceId)}/research`, {
		method: 'POST',
		body: input,
		timeoutMs: 60_000,
	});
}

export async function publishSpaceKnowledge(
	spaceId: string,
	entryId: string,
	published: boolean,
): Promise<GatewayResult<{ ok: boolean; entry?: SpaceKnowledgeEntry }>> {
	return readGateway(
		`/api/scout/gateway/spaces/${encodeURIComponent(spaceId)}/knowledge/${encodeURIComponent(entryId)}/publish`,
		{ method: 'POST', body: { published }, timeoutMs: 30_000 },
	);
}

// ------------------------------------------- shared trials, methods, programs

export interface ResearchTrialSummary {
	trialId: string;
	question: string;
	hypothesis: string;
	prediction: string;
	status: string;
	verdict: string | null;
	verdictReason: string | null;
	createdAt: string;
	evaluatedAt: string | null;
}

export interface ResearchTrialDetail {
	trial: ResearchTrialSummary & {
		falsifier: string;
		designHash: string;
		evaluation: {
			verdict: string;
			reason: string;
			dimensions: Array<{
				split: string;
				candidateArmId: string;
				baselineArmId: string;
				delta: number | null;
				deltaCi95: [number, number] | null;
				candidate: {
					mean: number | null;
					scored: number;
					nonReadings: number;
					ci95: [number, number] | null;
				};
				baseline: {
					mean: number | null;
					scored: number;
					nonReadings: number;
					ci95: [number, number] | null;
				};
			}>;
			limitations: string;
			holdoutConfirmed: boolean;
		} | null;
	};
	observations: Array<{
		observationId: string;
		armId: string;
		caseId: string;
		split: string;
		purpose: string;
		repeat: number;
		status: string;
		score: number | null;
		maxScore: number;
		scorer: string;
		scorerProvider: string | null;
		scorerModel: string | null;
		provider: string | null;
		model: string | null;
		latencyMs: number | null;
		costMicrousd: number | null;
		detail: string;
	}>;
}

export interface MethodCandidate {
	candidateId: string;
	name: string;
	version: number;
	status: string;
	specHash: string;
	trialId: string;
	evidenceRefs: string[];
	applicability: string;
	limitations: string;
	createdAt: string;
	updatedAt: string;
}

export interface CapabilityAdoption {
	adoptionId: string;
	candidateId: string;
	candidateVersion: number;
	target: string;
	status: string;
	verificationTrialId: string | null;
	adoptedAt: string;
	verifiedAt: string | null;
}

export interface PerspectiveRevision {
	revisionId: string;
	spaceId: string;
	fromVersion: number;
	toVersion: number;
	changeKind: string;
	statement: string;
	evidenceRefs: string[];
	createdAt: string;
}

export async function listPerspectiveRevisions(
	spaceId: string,
): Promise<GatewayResult<{ ok: boolean; revisions?: PerspectiveRevision[]; reason?: string }>> {
	return readGateway(
		`/api/scout/gateway/spaces/${encodeURIComponent(spaceId)}/perspective-revisions?limit=50`,
		{ method: 'GET', timeoutMs: 30_000 },
	);
}

export interface ResearchProgram {
	programId: string;
	spaceId: string | null;
	name: string;
	objective: string;
	status: string;
	budgetTotalMicrousd: number;
	budgetPerRunMicrousd: number;
	budgetPerDayMicrousd: number | null;
	startsAt: string;
	endsAt: string;
	maxRuns: number;
	schedule: SpaceSchedule | null;
	evidenceGrants: Array<{ kind: string; name: string; scope: string | null }>;
	stopReason: string | null;
}

export interface ProviderGrant {
	provider: string;
	status: string;
	allowedModels: string[];
	maxUsdPerCall: number;
	note: string | null;
	authorizedAt: string | null;
}

export interface ProviderStatus {
	provider: string;
	credentialName: string;
	credentialConfigured: boolean;
	grantStatus: string;
	maxUsdPerCall: number | null;
	allowedModels: string[];
	models: Array<{
		model: string;
		displayName: string;
		contextTokens: number;
		modalities: string[];
		capabilityBasis: string;
	}>;
}

export interface ModelCapabilityGap {
	capability: string;
	reason: string;
}

/** The answer to the free provider read. `credentialConfigured` is presence;
 *  `outcome` is what the provider actually said. */
export interface ProviderReachability {
	provider: string;
	/** The specific model checked, when the caller named one; NULL for the
	 *  model-list read. */
	model: string | null;
	/** True/False only for a specific-model read; NULL for a list read. */
	modelAvailable: boolean | null;
	credentialName: string;
	credentialConfigured: boolean;
	outcome:
		| 'REACHABLE'
		| 'CREDENTIAL_REFUSED'
		| 'UNREACHABLE'
		| 'NOT_CONFIGURED'
		| 'MODEL_NOT_AVAILABLE';
	httpStatus: number | null;
	models: string[];
	detail: string;
	checkedAt: string;
	/** What this read does and does not prove. Carried by the backend so the
	 *  surface cannot upgrade a model-list read into a generation claim. */
	proves: string;
	spend: string;
}

export async function probeProviderReachability(
	provider: string,
	model?: string,
): Promise<GatewayResult<{ ok: boolean; reachability?: ProviderReachability; reason?: string }>> {
	return readGateway('/api/scout/gateway/model/providers/reachability', {
		method: 'POST',
		body: { provider, ...(model === undefined ? {} : { model }) },
		timeoutMs: 20_000,
	});
}

export async function listResearchTrials(
	spaceId: string,
): Promise<
	GatewayResult<{
		ok: boolean;
		wired?: boolean;
		trials?: ResearchTrialSummary[];
		reason?: string;
	}>
> {
	return readGateway(
		`/api/scout/gateway/research/trials?spaceId=${encodeURIComponent(spaceId)}&limit=25`,
		{ method: 'GET', timeoutMs: 30_000 },
	);
}

export async function readResearchTrial(
	trialId: string,
): Promise<GatewayResult<{ ok: boolean; trial?: ResearchTrialDetail; reason?: string }>> {
	return readGateway(`/api/scout/gateway/research/trials/${encodeURIComponent(trialId)}`, {
		method: 'GET',
		timeoutMs: 30_000,
	});
}

export async function listMethodCandidates(
	spaceId: string,
): Promise<GatewayResult<{ ok: boolean; candidates?: MethodCandidate[]; reason?: string }>> {
	return readGateway(
		`/api/scout/gateway/research/methods?spaceId=${encodeURIComponent(spaceId)}&limit=25`,
		{ method: 'GET', timeoutMs: 30_000 },
	);
}

export async function validateMethodCandidate(
	candidateId: string,
	validationTrialId?: string | null,
): Promise<GatewayResult<{ ok: boolean; candidate?: MethodCandidate; reason?: string }>> {
	return readGateway(
		`/api/scout/gateway/research/methods/${encodeURIComponent(candidateId)}/validate`,
		{
			method: 'POST',
			body:
				typeof validationTrialId === 'string' && validationTrialId.trim().length > 0
					? { trialId: validationTrialId.trim() }
					: {},
			timeoutMs: 30_000,
		},
	);
}

export interface CandidateNoveltyReview {
	reviewId: string;
	candidateId: string;
	candidateVersion: number;
	query: string;
	searchProvider: string | null;
	priorArt: Array<{ title: string; url: string; snippet: string | null }>;
	judgeProvider: string;
	judgeModel: string;
	verdict: 'DISTINCT' | 'CLOSE' | 'SAME' | 'INCONCLUSIVE' | null;
	rationale: string;
	status: 'COMPLETED' | 'BLOCKED' | 'FAILED';
	detail: string;
	createdAt: string;
}

export async function listCandidateReviews(
	candidateId: string,
): Promise<GatewayResult<{ ok: boolean; reviews?: CandidateNoveltyReview[]; reason?: string }>> {
	return readGateway(
		`/api/scout/gateway/research/methods/${encodeURIComponent(candidateId)}/reviews`,
		{ method: 'GET', timeoutMs: 30_000 },
	);
}

export async function reviewCandidateNovelty(
	candidateId: string,
	input: { judgeProvider: string; judgeModel: string; query?: string; maxUsdPerCall?: number },
): Promise<GatewayResult<{ ok: boolean; review?: CandidateNoveltyReview; reason?: string }>> {
	return readGateway(
		`/api/scout/gateway/research/methods/${encodeURIComponent(candidateId)}/novelty`,
		{ method: 'POST', body: input, timeoutMs: 120_000 },
	);
}

export async function adoptMethodCandidate(
	candidateId: string,
	input: { target: string; note: string },
): Promise<GatewayResult<{ ok: boolean; adoption?: CapabilityAdoption; reason?: string }>> {
	return readGateway(
		`/api/scout/gateway/research/methods/${encodeURIComponent(candidateId)}/adopt`,
		{ method: 'POST', body: input, timeoutMs: 30_000 },
	);
}

export async function verifyCapabilityAdoption(
	adoptionId: string,
	verificationTrialId: string,
): Promise<GatewayResult<{ ok: boolean; adoption?: CapabilityAdoption; reason?: string }>> {
	return readGateway(
		`/api/scout/gateway/research/adoptions/${encodeURIComponent(adoptionId)}/verify`,
		{ method: 'POST', body: { verificationTrialId }, timeoutMs: 60_000 },
	);
}

export async function listCapabilityAdoptions(
	candidateId?: string,
): Promise<GatewayResult<{ ok: boolean; adoptions?: CapabilityAdoption[]; reason?: string }>> {
	const suffix =
		candidateId === undefined ? '' : `?candidateId=${encodeURIComponent(candidateId)}`;
	return readGateway(`/api/scout/gateway/research/adoptions${suffix}`, {
		method: 'GET',
		timeoutMs: 30_000,
	});
}

export async function listResearchPrograms(
	spaceId: string,
): Promise<GatewayResult<{ ok: boolean; programs?: ResearchProgram[]; reason?: string }>> {
	return readGateway(
		`/api/scout/gateway/research/programs?spaceId=${encodeURIComponent(spaceId)}`,
		{ method: 'GET', timeoutMs: 30_000 },
	);
}

export async function createResearchProgram(input: {
	name: string;
	objective: string;
	budgetTotalUsd: number;
	budgetPerRunUsd: number;
	budgetPerDayUsd?: number;
	startsAt: string;
	endsAt: string;
	maxRuns: number;
	spaceId?: string;
	schedule?: SpaceSchedule;
}): Promise<GatewayResult<{ ok: boolean; program?: ResearchProgram; reason?: string }>> {
	return readGateway('/api/scout/gateway/research/programs', {
		method: 'POST',
		body: input,
		timeoutMs: 30_000,
	});
}

export async function setResearchProgramStatus(
	programId: string,
	action: 'pause' | 'resume' | 'stop',
	reason?: string,
): Promise<GatewayResult<{ ok: boolean; program?: ResearchProgram; reason?: string }>> {
	return readGateway(
		`/api/scout/gateway/research/programs/${encodeURIComponent(programId)}/${action}`,
		{ method: 'POST', body: reason === undefined ? {} : { reason }, timeoutMs: 30_000 },
	);
}

export async function startResearchProgram(
	programId: string,
	input: { durationMinutes?: number; requestedBudgetUsd?: number } = {},
): Promise<
	GatewayResult<{
		ok: boolean;
		runId?: string;
		ceilingMicrousd?: number;
		reason?: string;
	}>
> {
	return readGateway(
		`/api/scout/gateway/research/programs/${encodeURIComponent(programId)}/start`,
		{ method: 'POST', body: input, timeoutMs: 60_000 },
	);
}

export async function listModelProviderGrants(): Promise<
	GatewayResult<{
		ok: boolean;
		grants?: ProviderGrant[];
		providers?: ProviderStatus[];
		gaps?: ModelCapabilityGap[];
		reason?: string;
	}>
> {
	return readGateway('/api/scout/gateway/model/providers', { method: 'GET', timeoutMs: 30_000 });
}

export async function saveModelProviderGrant(input: {
	provider: string;
	allowedModels?: string[];
	maxUsdPerCall: number;
	note?: string;
}): Promise<GatewayResult<{ ok: boolean; grant?: ProviderGrant; reason?: string }>> {
	return readGateway('/api/scout/gateway/model/providers', {
		method: 'POST',
		body: input,
		timeoutMs: 30_000,
	});
}

export function spaceDossierUrl(
	spaceId: string,
	format: 'json' | 'markdown' = 'markdown',
): string {
	return `/api/scout/gateway/spaces/${encodeURIComponent(spaceId)}/dossier?format=${format}`;
}

export async function runSpaceModelTrial(
	spaceId: string,
	input: {
		provider: 'deepseek' | 'gemini';
		model?: string;
		prompt: string;
		system?: string;
		maxOutputTokens?: number;
	},
): Promise<GatewayResult<{ ok: boolean; trial?: SpaceTrial; reason?: string }>> {
	return readGateway(`/api/scout/gateway/spaces/${encodeURIComponent(spaceId)}/model-trial`, {
		method: 'POST',
		body: input,
		timeoutMs: 120_000,
	});
}
